import { initElementTimestamp } from '../initializers/general.js';

/**
 * Perform `Object.assign` with type safety.
 */

const objectAssign = (targetObj, sourceObj) => Object.assign(targetObj, sourceObj);
/**
 * Perform `Object.entries` with strongly typed keys.
 */

const objectEntries = obj => Object.entries(obj);
/**
 * Take an object, and convert it to an array of `key, value` objects.
 *
 * Example:
 * ```
 * { 'abc': 0, 'xyz': 10 }
 * ⬇️
 * [
 *   { key: 'abc', value: 0 },
 *   { key: 'xyz', value: 10 },
 * ]
 * ```
 */

const objToKeyValueArray = obj => {
  const entries = objectEntries(obj);
  return entries.map(entry => {
    const key = entry[0];
    const value = entry[1];
    return {
      key,
      value
    };
  });
};
function objectFilter(object, callback) {
  const entries = objectEntries(object);
  const filteredEntries = entries.filter(entry => {
    const [key, value] = entry;
    return callback(value, key);
  });
  return Object.fromEntries(filteredEntries);
}
function objectFilterWithKey(object, callback) {
  const entries = objectEntries(object);
  const filteredEntries = entries.filter(entry => {
    const key = entry[0];
    const value = entry[1];
    return callback(value, key);
  });
  return Object.fromEntries(filteredEntries);
}
/**
 * Given an object, return a new object with only the keys that are in the
 * passed enum.
 *
 * Example:
 * ```
 * filterObjectByEnum({ nurse: 1, pastaChef: 1 }, Role);
 * ⬇️
 * { nurse: 1 }
 * ```
 */

const filterObjectByEnum = (object, enumObj) => {
  const filteredObject = objectFilterWithKey(object, (_value, key) => Object.values(enumObj).includes(key));
  return filteredObject;
};
/**
 * Perform `Object.keys` with proper types.
 */

const objectKeys = obj => Object.keys(obj);
/**
 * Perform `Array.map` on the values of an object.
 *
 * TODO: The typing of this function does not work for types that include
 * specific optional keys, such as `FlowDataFetchers<ItineraryFlowParam>`
 */

const objectMap = (object, callback, keyCallback) => Object.fromEntries(Object.entries(object).map(([key, value]) => {
  var _a;

  return [(_a = keyCallback === null || keyCallback === void 0 ? void 0 : keyCallback(key, value)) !== null && _a !== void 0 ? _a : key, callback(value, key)];
}));
/**
 * Given an enum, generate an object using the enum values as keys, with each
 * value being the same.
 *
 * Example:
 * ```
 * createObjectFromEnum(Role, {})
 * ⬇️
 * { 'nurse': {}, 'patient': {}, ... }
 * ```
 */

const createObjectFromEnum = (enumObj, output) => {
  const newObject = objectMap(enumObj, enumVal => {
    if (output instanceof Function) return output(enumVal);
    return output;
  }, (_enumKey, enumVal) => enumVal);
  return newObject;
};
/**
 * Given an enum, generate an object using the enum values as keys, with each
 * value being determined by a passed callback.
 *
 * Example:
 * ```
 * createObjectFromEnum(Role, (key) => ({ role: key }))
 * ⬇️
 * { 'nurse': { role: 'nurse' }, 'patient': { role: 'patient' }, ... }
 * ```
 */

const createObjectFromEnumWithCallback = (enumObj, outputCallback) => {
  const newObject = objectMap(enumObj, enumVal => outputCallback(enumVal), (_enumKey, enumVal) => enumVal);
  return newObject;
};
/**
 * Perform `Array.reduce` on the values of an object.
 */

const objectReduce = (object, callback, initialValue) => Object.entries(object).reduce((prev, curr) => callback(prev, curr), initialValue);
/**
 * Perform `Array.some` on the values of an object.
 */

const objectSome = (object, callback) => Object.values(object).some(element => callback(element));
/**
 * Takes an array of objects, and fills keys that are missing between objects
 * with empty strings. Suitable for preparing data for csv-stringify.
 *
 * Example:
 * ```
 * [{ id: 1, name: 'Tim' }{ id: 2, age: 20 }]
 * ⬇️
 * [{ id: 1, age: '', name: 'Tim' }{ id: 2, age: 20, name: '' }]
 * ```
 */

const getObjectsWithConsistentKeys = objects => {
  const emptyKeysObj = objects.reduce((latestKeysObj, object) => {
    const keysInCurrentObj = objectMap(object, () => '');
    return Object.assign(Object.assign({}, latestKeysObj), keysInCurrentObj);
  }, {});
  const updatedObjects = objects.map(object => Object.assign(Object.assign({}, emptyKeysObj), object));
  return updatedObjects;
};
/**
 * ByID Boolean to ByID ElementTimestamp
 *
 * Eg:
 *
 * { 1: true }
 *
 * to
 *
 * { 1: { id: 1, timestamp: Date.now() }
 */

const byIDBoolToByIDElementTimestamp = obj => {
  const elementTimestamp = {};
  Object.keys(obj).forEach(id => {
    elementTimestamp[id] = initElementTimestamp(id);
  });
  return elementTimestamp;
};
/**
 * Function to recursively traverse any object and convert any undefined value
 * to null. This is necessary because firebase does not allow undefined values.
 */

const undefinedToNull = obj => {
  if (obj === undefined) return null;
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map(undefinedToNull); // eslint-disable-next-line @typescript-eslint/no-unused-vars

  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, undefinedToNull(v)]));
};
/**
 * Recursively cleans an input object by filling in missing keys with values from an initializer object.
 * This ensures that the returned object has all the keys from the initializer, with any missing values
 * in the input filled in from the initializer. If a key's value is an object, it will recursively clean
 * that object as well.
 */

const cleanInputWithInitializerDeep = (input, initializer) => {
  const result = Object.assign({}, input);
  const keys = new Set([...Object.keys(input), ...Object.keys(initializer)]);
  keys.forEach(_key => {
    const key = _key;
    const inputVal = input[key];
    const initVal = initializer[key];

    if (initVal !== null && typeof initVal === 'object' && !Array.isArray(initVal)) {
      result[key] = cleanInputWithInitializerDeep(inputVal !== null && inputVal !== void 0 ? inputVal : {}, initVal);
    } else {
      result[key] = inputVal !== undefined ? inputVal : initVal;
    }
  });
  return result;
};

export { byIDBoolToByIDElementTimestamp, cleanInputWithInitializerDeep, createObjectFromEnum, createObjectFromEnumWithCallback, filterObjectByEnum, getObjectsWithConsistentKeys, objToKeyValueArray, objectAssign, objectEntries, objectFilter, objectFilterWithKey, objectKeys, objectMap, objectReduce, objectSome, undefinedToNull };
